import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return x.toISOString().slice(0,10);
}

export default function MeTimesheet() {
  const [week, setWeek] = useState(mondayOf(new Date()));
  const [entries, setEntries] = useState(DAYS.map(() => ({ hours: 0, project: "", notes: "" })));
  const [past, setPast] = useState([]);

  const load = () => api.get("/hr/timesheets/mine").then(r=>setPast(r.data)).catch(()=>setPast([]));
  useEffect(() => { load(); }, []);

  const total = entries.reduce((s,e)=>s + (+e.hours || 0), 0);

  const submit = async () => {
    const payload = {
      week_start: week,
      entries: DAYS.map((d, i) => {
        const dt = new Date(week); dt.setDate(dt.getDate() + i);
        return { date: dt.toISOString().slice(0,10), day: d, hours: +entries[i].hours || 0, project: entries[i].project, notes: entries[i].notes };
      }),
    };
    await api.post("/hr/timesheets", payload);
    toast.success("Timesheet submitted");
    load();
  };

  return (
    <div className="space-y-4" data-testid="me-timesheet">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Time &amp; effort</div>
        <h1 className="text-3xl font-bold font-display mt-1">Weekly timesheet</h1>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4 gap-3">
            <div>
              <div className="text-xs text-neutral-500">Week starting</div>
              <Input type="date" value={week} onChange={e=>setWeek(e.target.value)} data-testid="ts-week" className="w-40 mt-1" />
            </div>
            <div className="text-right">
              <div className="text-xs text-neutral-500">Total</div>
              <div className="text-2xl font-bold font-mono">{total}h</div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-neutral-500">
              <tr><th className="text-left py-2">Day</th><th className="text-left">Hours</th><th className="text-left">Project</th><th className="text-left">Notes</th></tr>
            </thead>
            <tbody>
              {DAYS.map((d, i) => (
                <tr key={d} className="border-t" data-testid={`ts-day-${d}`}>
                  <td className="py-2 font-medium">{d}</td>
                  <td><Input type="number" step="0.5" value={entries[i].hours} onChange={e=>{const c=[...entries];c[i].hours=e.target.value;setEntries(c);}} className="w-20 h-8" data-testid={`ts-hours-${d}`} /></td>
                  <td><Input value={entries[i].project} onChange={e=>{const c=[...entries];c[i].project=e.target.value;setEntries(c);}} className="h-8" placeholder="Project" /></td>
                  <td><Input value={entries[i].notes} onChange={e=>{const c=[...entries];c[i].notes=e.target.value;setEntries(c);}} className="h-8" placeholder="Notes" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={submit} className="btn-primary rounded-full mt-4" data-testid="ts-submit">Submit for approval</Button>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-3">Past weeks</h3>
          <div className="space-y-2">
            {past.length === 0 && <div className="text-sm text-neutral-500">No prior weeks yet.</div>}
            {past.map(t => (
              <div key={t.ts_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div><div className="text-sm font-semibold">Week of {t.week_start}</div><div className="text-xs text-neutral-500 mt-0.5">{t.total_hours}h · submitted {new Date(t.submitted_at).toLocaleDateString()}</div></div>
                <Badge variant="outline" className={t.status==="APPROVED"?"bg-teal/10 text-teal border-teal/20":"bg-neutral-100"}>{t.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
